import { Brain, Activity, FileText, AlertCircle, Clock, Users, HeartPulse, Stethoscope } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../../components/Hero';

export function TraumaticBrainInjuries() {
  return (
    <div>
      <Hero
        title="Traumatic Brain Injuries"
        description="Expert diagnosis and treatment for patients with traumatic brain injuries. Our comprehensive approach focuses on accurate assessment, effective medical management, and personalized rehabilitation strategies."
        image="https://images.unsplash.com/photo-1583912267550-d6cc3c410051?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Comprehensive TBI Care</h2>
            <p className="text-gray-600 mb-6">
              Our expert team provides specialized care for all types of traumatic brain injuries,
              from mild concussions to severe trauma. We understand that each injury is unique and
              requires a personalized approach to diagnosis and treatment.
            </p>
            <p className="text-gray-600">
              Using advanced diagnostic tools and evidence-based treatment techniques, we work to provide
              accurate diagnosis, effective medical management, and comprehensive rehabilitation support
              for optimal recovery outcomes.
            </p>
          </div>
          <div className="bg-gradient-cool rounded-lg p-8 shadow-glow">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Types of TBI We Treat</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <Brain className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Concussions</h4>
                  <p className="text-gray-600">Mild traumatic brain injuries affecting cognitive function, balance, and mood</p>
                </div>
              </li>
              <li className="flex items-start">
                <Activity className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Contusions</h4>
                  <p className="text-gray-600">Brain bruising requiring careful monitoring and specialized treatment protocols</p>
                </div>
              </li>
              <li className="flex items-start">
                <AlertCircle className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Diffuse Axonal Injury</h4>
                  <p className="text-gray-600">Widespread damage to brain connections requiring intensive medical intervention</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Common Symptoms</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80"
                  alt="Physical symptoms"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Physical Symptoms</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Headaches and migraines</li>
                <li>• Dizziness and balance issues</li>
                <li>• Vision problems</li>
                <li>• Fatigue and sleep disturbances</li>
                <li>• Sensitivity to light and sound</li>
                <li>• Nausea and vomiting</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&q=80"
                  alt="Cognitive symptoms"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Cognitive Symptoms</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Memory problems</li>
                <li>• Difficulty concentrating</li>
                <li>• Confusion and disorientation</li>
                <li>• Slow processing speed</li>
                <li>• Executive function issues</li>
                <li>• Language and communication difficulties</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1527689638836-411945a2b57c?auto=format&fit=crop&q=80"
                  alt="Emotional symptoms"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Emotional Symptoms</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Anxiety and depression</li>
                <li>• Mood swings</li>
                <li>• Irritability</li>
                <li>• Emotional lability</li>
                <li>• Personality changes</li>
                <li>• Post-traumatic stress</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-healing rounded-lg p-8 mb-16 shadow-glow">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Treatment Approach</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Initial Assessment</h3>
              <p className="text-gray-600 mb-4">
                Our comprehensive evaluation includes:
              </p>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>• Detailed neurological examination</li>
                <li>• Cognitive function testing</li>
                <li>• Balance and vestibular assessment</li>
                <li>• Advanced neuroimaging when indicated</li>
                <li>• Psychological evaluation</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Treatment Planning</h3>
              <p className="text-gray-600 mb-4">
                Personalized treatment plans may include:
              </p>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li>• Medication management</li>
                <li>• Cognitive rehabilitation</li>
                <li>• Physical therapy</li>
                <li>• Occupational therapy</li>
                <li>• Speech and language therapy</li>
                <li>• Psychological support</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Specialized Services</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <Clock className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Post-Acute Care</h3>
              <p className="text-gray-600">
                Structured rehabilitation programs designed for patients transitioning from acute care,
                focusing on functional recovery and independence.
              </p>
              <img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80"
                alt="Rehabilitation therapy"
                className="mt-4 w-full h-48 object-cover rounded-lg"
              />
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <Users className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Family Support</h3>
              <p className="text-gray-600">
                Comprehensive education and support for families and caregivers, including coping
                strategies and home care guidance.
              </p>
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80"
                alt="Family support"
                className="mt-4 w-full h-48 object-cover rounded-lg"
              />
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <Stethoscope className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Long-term Management</h3>
              <p className="text-gray-600">
                Ongoing monitoring and adjustment of treatment plans to address evolving symptoms
                and support continued recovery.
              </p>
              <img
                src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&q=80"
                alt="Patient monitoring"
                className="mt-4 w-full h-48 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Begin Your Recovery Journey</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Early intervention and appropriate treatment are crucial for optimal recovery from traumatic
            brain injury. Our experienced team is here to guide you through every step of your
            recovery journey.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/contact"
              className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
            >
              Schedule a Consultation
            </Link>
            <Link
              to="/patient-info/new-patients"
              className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
            >
              New Patient Information
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}